import { Box, ColorOptions, LoaderNew, ModBox, TextNew } from '@jfs/ui-new'
import { useIsFocused } from '@react-navigation/native'
import * as React from 'react'
import { FC, memo, useEffect, useRef, useState } from 'react'
import { InteractionManager } from 'react-native'
import { useLazyApiData, useStores } from '../../data/store'
import { FORM_FIELDS } from '../../screens/DomesticMoneyTransfer/Otp/OtpForm'
import { refundTheme } from '../../util/customTheme'
import { getNetworkResponseError } from '../../util/utill'
import { AepsTextInput } from '../AepsTextInput'
import styles from './styles'
import { TextInputProps } from './types'

const ERROR_COLOR = refundTheme.colors.ERROR as ColorOptions
//COUNTDOWN TIMER VALUE
const COUNTDOWN = 30
const COUNTDOWN_TIMER = 0
const SECOND = 1000
const MAX_INPUT = 6
const MAX_RETRY = 3

const OtpTextInputWithTimer: FC<TextInputProps> = memo((props: TextInputProps) => {
  const { sendOtp, OtpError, updateAuthenticationOtpError, amountFromStore } = useStores((store) => ({
    sendOtp: store.moneyTransferSession.sendOtp,
    OtpError: store.moneyTransferSession.OtpError,
    updateAuthenticationOtpError: store.moneyTransferSession.updateAuthenticationOtpError,
    amountFromStore: store.transactionDetails.originalTransactionAmount,
  }))

  const {
    // mode,
    // purpose,
    hide = false
  } = props
  const mode = (props as any)?.options?.mode ?? (props as any)?.mode
  const purpose = (props as any)?.options?.purpose ?? (props as any)?.purpose
  const amount = amountFromStore
  // const hide = props?.hide ?? false
  //Optimise is touched by using ref
  let isTouched = React.useRef(false)
  //IS FOCUSED HOOK
  const isFocused = useIsFocused()
  //TEXT INPUT FOCUS
  const OtpInput = useRef<TextInputProps>(null)

  //VALUE OF TEXTFIELD
  const [otpValue, setValue] = useState(props?.value ?? '')
  //SETTING VALUE WHEN TEXT INPUT IS FOCUSED
  const [focused, setFocused] = useState(false)
  const [countDownTimer, setCountDownTimer] = useState(COUNTDOWN)
  const [error, toggleError] = useState('')

  const [lazySendOtp, sendOtpHandler] = useLazyApiData(sendOtp as any)

  const totalTimesResendButtonPressed = React.useRef(0)

  //ERROR CONDITION
  const isError = otpValue?.length < MAX_INPUT && !focused && isTouched?.current
  //CONDITION TO SHOW THE TIMER COMPONENT
  const showTimeTickling = countDownTimer > COUNTDOWN_TIMER && countDownTimer <= COUNTDOWN

  const isResendOtpLoading = sendOtpHandler?.state === 'pending'

  //THIS WILL START THE TIMER.
  useEffect(() => {
    let timer: any
    if (countDownTimer > 0) {
      timer = setInterval(() => setCountDownTimer(countDownTimer - 1), SECOND)
    }
    //Clear all the subscriptions and listeners on unmount
    return () => clearInterval(timer)
  }, [countDownTimer])

  //AUTO FOCUS THE TEXT INPUT
  useEffect(() => {
    // Must run after animations for keyboard to automatically open
    if (isFocused) {
      InteractionManager.runAfterInteractions(() => {
        if (OtpInput?.current) {
          OtpInput?.current?.focus()
        }
      })
    }
  }, [isFocused])

  useEffect(() => {
    if (sendOtpHandler?.state === 'rejected') {
      const destructuredError = getNetworkResponseError(sendOtpHandler?.data)?.message ?? ''
      toggleError(destructuredError)
    } else if (OtpError?.length > 2) {
      toggleError(OtpError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOtpHandler.state, OtpError])

  const onResendOtpButtonPress = () => {
    const passedMobileValue = props?.allValues?.[FORM_FIELDS.MOBILE] || (props as any)?.mobileNumber
    if (passedMobileValue?.length > 9) {
      lazySendOtp(passedMobileValue, { mode, purpose, amount })
    }
  }

  const onResendOtp = () => {
    onResendOtpButtonPress()
    setCountDownTimer(COUNTDOWN)
    setValue('')
    totalTimesResendButtonPressed.current = totalTimesResendButtonPressed.current + 1
  }

  //WHEN TEXT INPUT TEXT CHANGES
  const handleChange = (text: string) => {
    setValue(text)
    if (props.onChangeText) {
      props.onChangeText(text)
    }
    if (props.setFieldValue && props.fieldName) {
      props.setFieldValue(props.fieldName, text)
    }
  }

  //SETTING FOCUSED TO TRUE WHEN USER CLICKS ON TEXT INPUT
  const onFocus = () => {
    isTouched.current = true
    setFocused(true)
    toggleError('')
    updateAuthenticationOtpError('')
  }

  //SETTING FOCUSED TO FALSE WHEN USER CLICKS ON TEXT INPUT
  const onBlur = () => {
    setFocused(false)
  }

  //RIGHT COMPONENT OF TEXT INPUT
  const ResendComponent = () => {
    const resendPressLimit = totalTimesResendButtonPressed.current >= MAX_RETRY
    if (isResendOtpLoading) {
      return <LoaderNew type={'small'} color={'PRIMARY_STORNG'} />
    } else {
      return showTimeTickling ? (
        <TextNew {...styles.timerText} title={`00:${countDownTimer}`} />
      ) : (
        <ModBox flexDirection={'row'} show={!resendPressLimit} onPress={onResendOtp} underlayColor={'LIGHTEST_GREY'}>
          <TextNew {...styles.resendText} title={'RESEND'} />
          <TextNew {...styles.countText} title={` (${MAX_RETRY - totalTimesResendButtonPressed.current})`} />
        </ModBox>
      )
    }
  }

  if (hide) {
    return null
  }

  const showError = isError || error

  return (
    <Box {...styles.container} onPress={() => OtpInput?.current?.focus()} underlayColor="TRANSPARENT">
      <Box {...styles.subContainer}>
        <Box {...styles.wrap} underlayColor="TRANSPARENT">
          <Box {...styles.inputBox}>
            <AepsTextInput
              value={otpValue}
              onChangeText={handleChange}
              placeholder={'6 Digit OTP'}
              secureTextEntry
              onFocus={onFocus}
              onBlur={onBlur}
              rightComponent={<ResendComponent />}
              keyboardType="numeric"
              tintColor={showError ? ERROR_COLOR : (refundTheme.colors.PRIMARY as ColorOptions)}
              bottomLineColor={showError ? ERROR_COLOR : (refundTheme.colors.PRIMARY as ColorOptions)}
              placeholderTextColor={showError ? ERROR_COLOR : undefined}
              maxLength={6}
              color={showError ? ERROR_COLOR : undefined}
              disabled={isResendOtpLoading}
              errorColor={ERROR_COLOR}
              isError={showError}
              returnKeyType={otpValue?.length > 5 ? 'done' : 'next'}
            />
          </Box>
        </Box>
      </Box>
      <Box {...styles.errorView}>
        {showError && <TextNew {...styles.errorText}>{error?.length > 1 ? error : 'Invalid Otp'}</TextNew>}
      </Box>
    </Box>
  )
})

export { OtpTextInputWithTimer }

